/* eslint-disable func-names */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-this-in-sfc */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Text } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import { InputForm } from '../../inputs/forms/InputForm';
import { getFetchData, useFetch } from '../../../hooks/useFetch';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import { FormActions, FormRow, FormSection, FormShell } from '../../ui';
import { FileInputForm } from '../../inputs/forms/FileInputForm';
import { TextAreaInputForm } from '../../inputs/forms/TextAreaInputForm';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
import { getFormattedLabel } from '../../../utils/FormatFunctions';
import { useFormValidationToast } from '../../../services/actionToast';

interface TemplateFormProps {
  defaultValues?: any;
  view?: boolean;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup?: any;
}

export default function TemplateForm({ defaultValues = {}, view = false }: TemplateFormProps) {
  const baseUrl = '/v1/admin/extra-material-templates';
  const { requestApi } = useFetch(baseUrl);
  const {
    requestApi: requestClasses,
    data: dataClasses,
    loading: loadingClasses,
  } = useFetch('/v1/admin/classes');
  const {
    requestApi: requestCategories,
    data: dataCategories,
    loading: loadingCategories,
  } = useFetch('/v1/admin/categories');
  const { itemId } = useParams();
  const navigate = useNavigate();
  const validationToast = useFormValidationToast();

  useEffect(() => {
    requestClasses('get');
    requestCategories('get');
  }, []);

  const optionsClasses = (getFetchData(dataClasses)?.data ?? []).map((item: any) =>
    getFormattedLabel(item)
  );
  const optionsCategories = (getFetchData(dataCategories)?.data ?? []).map((item: any) =>
    getFormattedLabel(item)
  );

  const optionsUserType = [
    { label: 'Professor', value: 'PROFESSOR' },
    { label: 'Responsável', value: 'RESPONSAVEL' },
    { label: 'Todos', value: 'TODOS' },
  ];

  const optionsType = [
    { label: 'Mídia (arquivo)', value: 'MIDIA' },
    { label: 'Link', value: 'LINK' },
  ];

  const defaultUserTypeSelection = useMemo(() => {
    const allOption = optionsUserType.find((item) => item.value === 'TODOS');
    if (!defaultValues?.user_type) {
      return allOption ? [allOption] : [];
    }

    const rawSelections = Array.isArray(defaultValues.user_type)
      ? defaultValues.user_type
      : String(defaultValues.user_type)
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);

    const normalizedValues = rawSelections
      .map((entry) => (typeof entry === 'string' ? entry.toUpperCase() : entry?.value))
      .filter(Boolean);

    const matched = optionsUserType.filter((option) => normalizedValues.includes(option.value));
    if (matched.length === 0) {
      return allOption ? [allOption] : [];
    }

    if (matched.some((option) => option.value === 'TODOS')) {
      return allOption ? [allOption] : matched;
    }

    return matched;
  }, [defaultValues?.user_type, optionsUserType]);

  const validationSchema: any = {};
  const defaultSchemaValues: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Título do template',
      label: 'Título',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'description',
      value: defaultValues.description ?? '',
      placeholder: 'Descreva o material',
      label: 'Descrição',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'school_class_id',
      value: defaultValues?.school_classes
        ? defaultValues.school_classes.map((sc: any) => getFormattedLabel(sc))
        : defaultValues?.school_class
        ? [getFormattedLabel(defaultValues?.school_class)]
        : [],
      placeholder: 'Selecione um ou mais módulos',
      label: 'Módulo(s)',
      yup: Yup.array()
        .of(
          Yup.object().shape({
            value: Yup.mixed().required(),
            label: Yup.string().required(),
          })
        )
        .min(1, 'Selecione pelo menos um módulo')
        .required('Este campo é obrigatório!'),
    },
    {
      name: 'category_id',
      value: defaultValues?.category ? getFormattedLabel(defaultValues?.category) : null,
      placeholder: 'Selecione uma categoria',
      label: 'Categoria',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'user_type',
      value: defaultUserTypeSelection,
      placeholder: 'Selecione o público',
      label: 'Usuários que vão receber',
      yup: Yup.array()
        .of(
          Yup.object().shape({
            value: Yup.string().required(),
            label: Yup.string().required(),
          })
        )
        .min(1, 'Selecione pelo menos um público')
        .required('Este campo é obrigatório!'),
    },
    {
      name: 'type',
      value: defaultValues?.type
        ? optionsType.find((item) => item.value === defaultValues.type.toUpperCase())
        : optionsType[0],
      placeholder: 'Selecione o tipo do conteúdo',
      label: 'Formato do recurso',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'link',
      value: defaultValues.link ?? '',
      placeholder: 'https://exemplo.com/material',
      label: 'Link do material',
      yup: Yup.string().test('link-required', 'Informe o link do material', function (value: any) {
        if (this.parent.type?.value === 'LINK' || this.parent.type === 'LINK') {
          return value && value.trim().length > 0;
        }
        return true;
      }),
    },
    {
      name: 'file',
      value: defaultValues.file?.original ?? null,
      label: 'Anexo (upload)',
      yup: Yup.mixed()
        .nullable()
        .test('file-required', 'Selecione um arquivo', function (value) {
          if (this.parent.type?.value === 'MIDIA' || this.parent.type === 'MIDIA') {
            return value && value.length > 0;
          }
          return true;
        })
        .test('file-max-size', 'O arquivo deve ter no máximo 100MB', function (value) {
          if (!value || !value[0]) return true;
          const maxSizeInBytes = 100 * 1024 * 1024;
          return value[0].size <= maxSizeInBytes;
        }),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
    {
      name: 'is_downloadable',
      value: defaultValues.is_downloadable ?? true,
      label: 'Permitir download',
      yup: Yup.boolean(),
    },
    {
      name: 'is_official',
      value: defaultValues.is_official ?? true,
      label: 'Template oficial',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((field) => {
    validationSchema[field.name] = field.yup;
    defaultSchemaValues[field.name] = field.value;
  });

  const schema = Yup.object().shape(validationSchema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultSchemaValues,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const description = formInputs.find((field) => field.name === 'description');
  const schoolClass = formInputs.find((field) => field.name === 'school_class_id');
  const category = formInputs.find((field) => field.name === 'category_id');
  const userType = formInputs.find((field) => field.name === 'user_type');
  const type = formInputs.find((field) => field.name === 'type');
  const link = formInputs.find((field) => field.name === 'link');
  const file = formInputs.find((field) => field.name === 'file');
  const status = formInputs.find((field) => field.name === 'status');
  const isDownloadable = formInputs.find((field) => field.name === 'is_downloadable');
  const isOfficial = formInputs.find((field) => field.name === 'is_official');

  const submitForm = async (values: any) => {
    const treatLink = (rawLink: string | undefined) => {
      if (!rawLink) return null;
      if (rawLink.startsWith('http://') || rawLink.startsWith('https://')) return rawLink;
      return `https://${rawLink}`;
    };

    const selectedUserTypes = Array.isArray(values.user_type)
      ? values.user_type.map((item: any) => item.value)
      : values.user_type?.value
      ? [values.user_type.value]
      : [];

    const normalizedUserType =
      selectedUserTypes.length === 0 ||
      selectedUserTypes.includes('TODOS') ||
      selectedUserTypes.length === optionsUserType.length - 1
        ? 'TODOS'
        : selectedUserTypes.join(',');

    // Lidar com múltiplos módulos
    const selectedClasses = Array.isArray(values.school_class_id)
      ? values.school_class_id.map((item: any) => item.value)
      : [values.school_class_id.value];

    const payload: any = {
      name: values.name,
      description: values.description,
      type: values.type.value,
      user_type: normalizedUserType,
      school_class_ids: selectedClasses, // Mudança: agora envia array
      category_id: values.category_id.value,
      status: values.status,
      is_downloadable: values.is_downloadable,
      is_official: values.is_official,
      version: defaultValues.version ?? 1,
    };

    if (payload.type === 'LINK') {
      payload.link = treatLink(values.link);
    } else {
      payload.link = null;
    }

    const { status: resStatus, data } = await requestApi(
      defaultValues.name ? 'put' : 'post',
      payload,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );

    const shouldUploadFile =
      (values.file?.length || 0) > 0 && (values.type.value === 'MIDIA' || values.type === 'MIDIA');

    if ((resStatus === 200 || resStatus === 201) && shouldUploadFile) {
      const formData = new FormData();
      formData.append('file', values.file[0]);
      const templateId = itemId ?? data?.data?.id;
      await requestApi('put', formData, `${baseUrl}/${templateId}/files`, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }

    if (resStatus === 200 || resStatus === 201) navigate('/templates/list');
  };

  useEffect(() => {
    if (defaultValues?.name) {
      reset(defaultSchemaValues);
    }
  }, [defaultValues]);

  let titleAction = 'Criar';
  if (view) titleAction = 'Visualizar';
  else if (defaultValues.name) titleAction = 'Editar';

  return (
    <FormShell
      title={`${titleAction} Template de Material`}
      backTo="/templates/list"
      backLabel="Templates"
    >
      <Box
        as="form"
        noValidate
        onSubmit={handleSubmit(submitForm, validationToast)}
        display="flex"
        flexDirection="column"
        gap="16px"
      >
        <FormSection title="Informações básicas">
          <FormRow>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              isDisabled={view}
              register={register}
              errors={errors}
            />
            <SelectInputForm
              name={userType?.name}
              label={userType?.label}
              options={optionsUserType}
              isRequired
              isDisabled={view}
              isMulti
              control={control}
              errors={errors}
              placeholder="Selecione um ou mais públicos"
            />
            <SelectInputForm
              name={type?.name}
              label={type?.label}
              options={optionsType}
              isRequired
              isDisabled={view}
              control={control}
              errors={errors}
            />
          </FormRow>
        </FormSection>

        <FormSection title="Classificação">
          <FormRow>
            <SelectInputForm
              name={category?.name}
              label={category?.label}
              options={optionsCategories}
              isLoading={loadingCategories || !dataCategories}
              isDisabled={view}
              placeholder={category?.placeholder}
              control={control}
              errors={errors}
            />
            <Box>
              <SelectInputForm
                name={schoolClass?.name}
                label={schoolClass?.label}
                options={optionsClasses}
                isLoading={loadingClasses || !dataClasses}
                isDisabled={view}
                placeholder={
                  watch('category_id')?.label === 'FORMAÇÃO PARA PROFESSORES'
                    ? 'Selecione um módulo'
                    : 'Selecione um ou mais módulos'
                }
                control={control}
                errors={errors}
                isMulti={watch('category_id')?.label !== 'FORMAÇÃO PARA PROFESSORES'}
              />
              {watch('category_id')?.label !== 'FORMAÇÃO PARA PROFESSORES' && (
                <Text fontSize="12px" lineHeight="16px" color="content.body" mt="8px">
                  Você pode selecionar múltiplos módulos para este template
                </Text>
              )}
            </Box>
          </FormRow>
        </FormSection>

        <FormSection title="Conteúdo">
          <Box>
            <TextAreaInputForm
              name={description?.name}
              label={description?.label}
              placeholder={description?.placeholder}
              isRequired
              isDisabled={view}
              register={register}
              errors={errors}
            />
          </Box>

          {watch('type')?.value === 'LINK' || watch('type') === 'LINK' ? (
            <Box>
              <InputForm
                name={link?.name}
                label={link?.label}
                placeholder={link?.placeholder}
                isDisabled={view}
                register={register}
                errors={errors}
              />
            </Box>
          ) : (
            <Box>
              <FileInputForm
                defaultVal={file?.value}
                name={file?.name}
                label={file?.label}
                register={register}
                watch={watch}
                errors={errors}
                isRequired={!view}
              />
            </Box>
          )}
        </FormSection>

        <FormSection title="Configurações">
          <FormRow>
            <SwitchInputForm
              name={status?.name}
              label="Ativo"
              isDisabled={view}
              errors={errors}
              control={control}
            />
            <SwitchInputForm
              name={isDownloadable?.name}
              label={isDownloadable?.label}
              isDisabled={view}
              errors={errors}
              control={control}
            />
            <SwitchInputForm
              name={isOfficial?.name}
              label={isOfficial?.label}
              isDisabled={view}
              errors={errors}
              control={control}
            />
          </FormRow>
        </FormSection>

        {!view && (
          <FormActions
            cancelTo="/templates/list"
            isLoading={isSubmitting}
            submitLabel={defaultValues.name ? 'Salvar alterações' : 'Criar template'}
          />
        )}
      </Box>
    </FormShell>
  );
}
